/**
 * TransactionStatusIndicator
 *
 * Displays loading spinner, success check, or failure X based on transaction status.
 */

import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { ITransactionStatusIndicatorProps } from '../../types';
import { T_KEYS } from '../../constants';
import {
  StatusContainer,
  StatusSpinner,
  StatusIconCircle,
  StatusTitle,
  StatusDescription,
  StatusButton,
} from '../../styles';

const TransactionStatusIndicator = memo(({
  status,
  onRetry,
  onGoHome,
  onManageSubscriptions,
}: ITransactionStatusIndicatorProps) => {
  const { t } = useTranslation();

  if (status === 'loading' || status === 'processing') {
    return (
      <StatusContainer>
        <StatusSpinner />
        <StatusTitle>
          {status === 'loading' ? t(T_KEYS.VERIFYING_PAYMENT) : t(T_KEYS.PROCESSING_PAYMENT)}
        </StatusTitle>
        <StatusDescription>
          {t(T_KEYS.VERIFYING_PAYMENT_DESC)}
        </StatusDescription>
      </StatusContainer>
    );
  }

  if (status === 'success') {
    return (
      <StatusContainer>
        <StatusIconCircle $type="success">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="#3dd598" />
          </svg>
        </StatusIconCircle>
        <StatusTitle>{t(T_KEYS.PAYMENT_SUCCESSFUL)}</StatusTitle>
        <StatusDescription>{t(T_KEYS.PAYMENT_SUCCESSFUL_DESC)}</StatusDescription>
        {onManageSubscriptions && (
          <StatusButton onClick={onManageSubscriptions}>
            {t(T_KEYS.MANAGE_SUBSCRIPTIONS)}
          </StatusButton>
        )}
        {onGoHome && (
          <StatusButton $variant="outlined" onClick={onGoHome}>
            {t(T_KEYS.GO_HOME)}
          </StatusButton>
        )}
      </StatusContainer>
    );
  }

  if (status === 'failed') {
    return (
      <StatusContainer>
        <StatusIconCircle $type="fail">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <line x1="8" y1="8" x2="16" y2="16" stroke="#f44336" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="16" y1="8" x2="8" y2="16" stroke="#f44336" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </StatusIconCircle>
        <StatusTitle>{t(T_KEYS.PAYMENT_FAILED)}</StatusTitle>
        <StatusDescription>{t(T_KEYS.PAYMENT_FAILED_DESC)}</StatusDescription>
        {onRetry && (
          <StatusButton onClick={onRetry}>
            {t(T_KEYS.TRY_AGAIN)}
          </StatusButton>
        )}
        {onGoHome && (
          <StatusButton $variant="outlined" onClick={onGoHome}>
            {t(T_KEYS.GO_HOME)}
          </StatusButton>
        )}
      </StatusContainer>
    );
  }

  if (status === 'timeout') {
    return (
      <StatusContainer>
        <StatusIconCircle $type="fail">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="#f44336" strokeWidth="2" fill="none" />
            <path d="M12 7v5l3 3" stroke="#f44336" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </StatusIconCircle>
        <StatusTitle>{t(T_KEYS.PAYMENT_TIMEOUT)}</StatusTitle>
        <StatusDescription>{t(T_KEYS.PAYMENT_TIMEOUT_DESC)}</StatusDescription>
        {onRetry && (
          <StatusButton onClick={onRetry}>
            {t(T_KEYS.TRY_AGAIN)}
          </StatusButton>
        )}
        {onGoHome && (
          <StatusButton $variant="outlined" onClick={onGoHome}>
            {t(T_KEYS.GO_HOME)}
          </StatusButton>
        )}
      </StatusContainer>
    );
  }

  return null;
});

export default TransactionStatusIndicator;
